import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/outline";
import { useParams } from "react-router-dom";

import { useAuth } from "@/context/auth-context";

import { Column } from "@/components/SettingsTable/types";
import { useDeleteCustomModel } from "@/queries/models/useDeleteCustomModel";

import { useCustomParametersStore } from "../../CustomModelsCreation/CustomParameters/custom-parameters-store";
import { HUGGINGFACE_PROVIDER } from "@/components/ModelParameters/constants";
import { useDeleteInferenceClient, useInferenceClientsList } from "@/queries";
import { CustomModel } from "../../types";
import { ModelParameters } from "@/components/ModelParameters";
import { useCustomModels } from "@/queries/models/useCustomModels";
import { ProviderWithLogo } from "@/components/ProviderWithLogo";
import {
  ContextMenuItems,
  ContextMenuVariant,
} from "@/components/ui/ContextMenu/types";
import { mapParametersToPromptData } from "../utils";
import { DropdownMenuVariant, Action } from "@/components/DropdownMenu";

export const useCustomModelsTable = ({
  onEditModel,
}: {
  onEditModel: (model: CustomModel, isViewing?: boolean) => void;
}) => {
  const {
    data: models,
    isRefetching: isModelsRefetching,
    isLoading: isModelsLoading,
  } = useCustomModels();

  const store = useCustomParametersStore();
  const { workspaceId } = useParams();
  const authContext = useAuth();

  const { mutate: deleteModel, isLoading: isDeletingModel } =
    useDeleteCustomModel(authContext?.userToken ?? "");

  const { mutate: deleteInferenceClient } = useDeleteInferenceClient(
    authContext?.userToken ?? "",
  );

  const { data: inferenceClients } = useInferenceClientsList(
    authContext?.userToken!,
    workspaceId!,
  );

  const columns: Column<CustomModel>[] = [
    {
      header: "Custom Name",
      content: (model) => model.display_name,
    },
    {
      header: "Provider",
      content: (model) => <ProviderWithLogo provider={model.provider_name} />,
    },
    { header: "Model", content: (model) => model.llm_model_name },
    {
      header: "Type",
      content: (model) => (model.is_chat ? "Chat" : "Completion"),
    },
    {
      header: "Parameters",
      content: (model) =>
        model.params.length > 0 ? (
          <ModelParameters
            readonly
            promptData={{
              metadata: {
                model: {
                  name: model.llm_model_name,
                  provider: model.provider_name,
                  model_config_display_name: model.display_name,
                  parameters: mapParametersToPromptData(model.params),
                },
              },
              provider_id: model.provider_id,
            }}
            triggerVariant="outline"
            showIcon={false}
            showTooltip={false}
            triggerLabel="View Parameters"
            showLearnMore={false}
            showGeneralInformation={false}
            isPromptChat={model.is_chat}
          />
        ) : (
          "-"
        ),
    },
  ];

  const onViewModel = (model: CustomModel) => {
    const inferenceClient = inferenceClients?.inference_clients.find(
      (client) => client.name === model.display_name,
    );
    const cookies = Object.entries(inferenceClient?.cookies || {}).map(
      ([key, value], index) => ({
        id: index,
        key,
        value,
      }),
    );
    const headers = Object.entries(inferenceClient?.headers || {}).map(
      ([key, value], index) => ({
        id: index,
        key,
        value,
      }),
    );
    store.setCookies(cookies);
    store.setHeaders(headers);
    store.setTimeout(inferenceClient?.timeout || 1);
    onEditModel(model, true);
  };

  const onDeleteModel = (model: CustomModel) => {
    const inferenceClient = inferenceClients?.inference_clients.find(
      (client) => client.name === model.display_name,
    );

    if (inferenceClient) {
      deleteInferenceClient({ id: inferenceClient.id });
    }

    deleteModel(String(model.id));
  };

  const actions: Action<CustomModel>[] = [
    {
      id: "view",
      label: "View",
      icon: EyeIcon,
      onClick: (model) => onViewModel(model!),
    },
    {
      id: "edit",
      label: "Edit",
      icon: PencilIcon,
      onClick: (model) => onEditModel(model!),
      showIf: (model) => model?.provider_name !== HUGGINGFACE_PROVIDER,
    },
    {
      id: "delete",
      label: "Delete",
      variant: DropdownMenuVariant.DESTRUCTIVE,
      icon: TrashIcon,
      onClick: (model) => onDeleteModel(model!),
    },
  ];

  const contextMenuItems: ContextMenuItems<CustomModel>[] = [
    {
      id: "view",
      label: "View model",
      icon: EyeIcon,
      onClick: onViewModel,
    },
    {
      id: "edit",
      label: "Edit model",
      icon: PencilIcon,
      onClick: onEditModel,
      getDisabled: (model) => model.provider_name === HUGGINGFACE_PROVIDER,
      tooltipContent: "HuggingFace models cannot be edited",
    },
    {
      id: "delete",
      label: "Delete model",
      variant: ContextMenuVariant.DESTRUCTIVE,
      icon: TrashIcon,
      onClick: onDeleteModel,
      className:
        "text-red-600 group/delete hover:!bg-red-50 hover:!text-red-600",
    },
  ];

  return {
    columns,
    actions,
    contextMenuItems,
    isDeletingModel,
    isModelsLoading: isModelsRefetching || isModelsLoading,
    models: models?.model_configs || [],
  };
};
